import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { CloseOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import { useHistory } from "react-router-dom";

import CheckoutProcessingOverlay from "../../MyAccount/UsageMetrics/components/CheckoutProcessingOverlay";
import SuccessModal from "../../MyAccount/UsageMetrics/components/SuccessModal";
import { getUsageMetricsBuildPlanPath } from "../../MyAccount/UsageMetrics/constants";
import useGyngerCheckoutPayment, {
  GYNGER_SUCCESS_MODAL_AUTO_CLOSE_MS,
} from "../../MyAccount/UsageMetrics/hooks/useGyngerCheckoutPayment";
import CreditPurchaseActionCard from "./components/CreditPurchaseActionCard";
import CreditPurchaseBreakdownTable from "./components/CreditPurchaseBreakdownTable";
import {
  CREDIT_PURCHASE_RATES,
  CREDIT_PURCHASE_VARIANT_COPY,
} from "./constants";

import { isThirdPartyUser } from "../../../utils/common";
import { canMakePayment } from "../../../utils/Permissions";
import { setPlanBuilderPrefillCredits } from "../../MyAccount/UsageMetrics/utils";
import {
  buildCheckoutCreditsFromPurchaseRows,
  buildCreditPurchaseRowsFromClassify,
  formatCreditCurrency,
  getCreditPurchaseTotal,
  getCreditPurchaseTotalCredits,
} from "./utils";

import bankCardIcon from "../../../assets/images/subscriptions/bank-card-2-line.svg";
import flaskIcon from "../../../assets/images/subscriptions/flask-line.svg";
import testTubeIcon from "../../../assets/images/subscriptions/test-tube-negative.svg";

import "./CreditPurchaseModal.css";

const resolveCustomerTypeVariant = (customerType) => {
  if (customerType == null || customerType === "") {
    return null;
  }
  return String(customerType).toLowerCase();
};

export default function CreditPurchaseModal({
  open,
  onClose,
  onPayNow,
  onSelectTestQuantity,
  classifyResponse,
  availableCreditsByCode,
  variant: variantProp,
  rows = [],
  rates = CREDIT_PURCHASE_RATES,
  payLoading = false,
  title,
  subtitle,
  saveDraftOnSelectQuantity = true,
  selectQuantityDescription,
}) {
  const [draftSaving, setDraftSaving] = useState(false);
  const [showDraftSavedModal, setShowDraftSavedModal] = useState(false);
  const draftRedirectHandledRef = useRef(false);
  const draftSavedPendingRef = useRef(false);
  const history = useHistory();

  const handleCheckoutComplete = useCallback(async (response) => {
    if (onPayNow) {
      await onPayNow(response);
    }

    onClose();
  }, [onClose, onPayNow]);

  const {
    isCheckoutLoading,
    paymentUrl,
    showSuccessModal,
    handleSuccessClose,
    showFailedModal,
    handleFailedClose,
    startGyngerCheckout,
    resetCheckout,
  } = useGyngerCheckoutPayment({
    onComplete: handleCheckoutComplete,
  });

  useEffect(() => {
    if (!open) {
      resetCheckout();
      if (!draftSavedPendingRef.current) {
        setShowDraftSavedModal(false);
      }
      return undefined;
    }

    draftRedirectHandledRef.current = false;
    draftSavedPendingRef.current = false;
    setShowDraftSavedModal(false);
    return undefined;
  }, [open, resetCheckout]);

  const classifyCustomerType = resolveCustomerTypeVariant(
    classifyResponse?.customer_type ?? classifyResponse?.result?.customer_type,
  );
  const variant = resolveCustomerTypeVariant(variantProp)
    ?? classifyCustomerType;
  const resolvedVariant = CREDIT_PURCHASE_VARIANT_COPY[variant] ? variant : "prepaid";
  const copy = CREDIT_PURCHASE_VARIANT_COPY[resolvedVariant];
  const headingTitle = title ?? copy.title;
  const headingSubtitle = subtitle ?? copy.subtitle;
  const breakdownRows = useMemo(
    () => buildCreditPurchaseRowsFromClassify(classifyResponse, {
      availableCreditsByCode,
    }) || rows,
    [availableCreditsByCode, classifyResponse, rows],
  );
  const totalAmount = useMemo(
    () => getCreditPurchaseTotal(breakdownRows, rates),
    [breakdownRows, rates],
  );
  const totalCredits = useMemo(
    () => getCreditPurchaseTotalCredits(breakdownRows),
    [breakdownRows],
  );
  const checkoutCredits = useMemo(
    () => buildCheckoutCreditsFromPurchaseRows(breakdownRows),
    [breakdownRows],
  );
  const isPayLoading = payLoading || isCheckoutLoading;
  const hasPurchaseRows = breakdownRows.length > 0;
  const isInteractionLocked = isPayLoading || draftSaving;
  const areActionsDisabled = isInteractionLocked || !hasPurchaseRows;
  const isPayable = totalAmount > 0 && checkoutCredits.length > 0;
  const allowPayment = canMakePayment() && !isThirdPartyUser();

  const redirectToPlanBuilder = useCallback(() => {
    if (draftRedirectHandledRef.current) {
      return;
    }

    draftRedirectHandledRef.current = true;
    setShowDraftSavedModal(false);
    onClose();
    history.push(getUsageMetricsBuildPlanPath());
  }, [history, onClose]);

  useEffect(() => {
    if (!showDraftSavedModal) {
      return undefined;
    }

    const timerId = setTimeout(() => {
      redirectToPlanBuilder();
    }, GYNGER_SUCCESS_MODAL_AUTO_CLOSE_MS);

    return () => {
      clearTimeout(timerId);
    };
  }, [redirectToPlanBuilder, showDraftSavedModal]);

  const handlePayClick = async () => {
    if (areActionsDisabled || !isPayable) {
      return;
    }

    await startGyngerCheckout({
      amount: totalAmount,
      credits: checkoutCredits,
      purpose: "recharge",
    });
  };

  const handleSelectTestQuantity = async () => {
    if (areActionsDisabled) {
      return;
    }

    setDraftSaving(true);

    try {
      if (onSelectTestQuantity) {
        const result = await onSelectTestQuantity();
        if (result && result.success === false) {
          return;
        }
      }

      setPlanBuilderPrefillCredits(buildCheckoutCreditsFromPurchaseRows(breakdownRows));
      if (!saveDraftOnSelectQuantity) {
        redirectToPlanBuilder();
        return;
      }
      draftSavedPendingRef.current = true;
      setShowDraftSavedModal(true);
      onClose();
    } finally {
      setDraftSaving(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={isInteractionLocked ? undefined : onClose}
        footer={null}
        closable={false}
        maskClosable={!isInteractionLocked}
        keyboard={!isInteractionLocked}
        centered
        destroyOnHidden
        width="100%"
        wrapClassName="CreditPurchaseModal"
        className={`CreditPurchaseModal CreditPurchaseModal--${resolvedVariant}`}
      >
        <header className="CreditPurchaseModal__Header">
          <div className="CreditPurchaseModal__Heading">
            <span className="CreditPurchaseModal__HeaderIcon" aria-hidden>
              <img src={testTubeIcon} alt="" />
            </span>
            <div className="CreditPurchaseModal__HeadingCopy">
              <h2 className="CreditPurchaseModal__Title">{headingTitle}</h2>
              <p className="CreditPurchaseModal__Subtitle">{headingSubtitle}</p>
            </div>
          </div>
          {!isInteractionLocked && (
            <button
              type="button"
              className="CreditPurchaseModal__CloseButton"
              onClick={onClose}
              aria-label="Close credit purchase modal"
            >
              <CloseOutlined />
            </button>
          )}
        </header>

        <div
          className="CreditPurchaseModal__Body"
          aria-busy={isPayLoading}
        >
          <CreditPurchaseBreakdownTable rows={breakdownRows} rates={rates} />

          {allowPayment ? (
            <div className="CreditPurchaseModal__Actions">
              <CreditPurchaseActionCard
                icon={<img src={bankCardIcon} alt="" />}
                title={copy.payTitle}
                description={copy.payDescription}
                actionLabel={`Pay ${formatCreditCurrency(totalAmount)} Now`}
                actionType="primary"
                onAction={handlePayClick}
                loading={isPayLoading}
                disabled={areActionsDisabled || !isPayable}
              />
              <CreditPurchaseActionCard
                icon={<img src={flaskIcon} alt="" />}
                title={copy.secondaryTitle}
                description={selectQuantityDescription ?? copy.secondaryDescription}
                actionLabel={copy.secondaryActionLabel}
                actionType="text"
                onAction={handleSelectTestQuantity}
                loading={draftSaving}
                disabled={areActionsDisabled}
              />
            </div>
          ) : (
            <CreditPurchaseActionCard
              description="Don't have enough credits to make a submission. Please reach out to your admin for purchase of credits."
            />
          )}
        </div>

        <CheckoutProcessingOverlay
          open={isPayLoading}
          onCancelPayment={resetCheckout}
          paymentUrl={paymentUrl}
        />
      </Modal>

      <SuccessModal
        open={showSuccessModal}
        onClose={handleSuccessClose}
        title="Payment Successful!"
        description={`${Number(totalCredits || 0).toLocaleString()} test credits have been added to your account.`}
      />
      <SuccessModal
        open={showFailedModal}
        onClose={handleFailedClose}
        variant="failed"
        title="Payment Failed"
        description="Your payment could not be completed. Please try again."
      />
      <SuccessModal
        open={showDraftSavedModal}
        onClose={redirectToPlanBuilder}
        title="Saved under drafts!"
        description=""
      />
    </>
  );
}
